import React from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Grid2, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import {
  OpsTatPath,
  OpsTatStatus,
} from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { OpsTatMetricId } from "../../../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { OpsTatStageCard } from "../../../../../../models/Dashboard/OpsTat/OpsTatSummary";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import {
  formatOpsTatAsOf,
  formatOpsTatCount,
  formatOpsTatDays,
  formatOpsTatDeltaLabel,
  formatOpsTatNumber,
  formatOpsTatPathEyebrow,
  formatOpsTatPercent,
  formatOpsTatSignedDays,
  getOpsTatSpineFlex,
  getOpsTatSpineTrackMinWidth,
  getOpsTatStatusColors,
  getOpsTatStatusPillSx,
  OPS_TAT_SPINE_MIN_PX,
} from "./opsTatFormat";

interface OpsTatDaysLedgerProps {
  store: NewOperationsDashboardStore;
}

const PATH_OPTIONS: Array<{ value: OpsTatPath; label: string }> = [
  { value: "all", label: "All" },
  { value: "custom", label: "Custom · 8 stages" },
  { value: "stock", label: "Stock · 5 stages" },
];

const clickableSx = {
  cursor: "pointer",
  outline: "none",
};

const OpsTatInlineState = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="10rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const StatusDot = ({ status }: { status: OpsTatStatus | undefined }) => {
  const theme = useTheme();
  const colors = getOpsTatStatusColors(theme, status);
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: colors.fg,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
};

const OpsTatDaysLedger: React.FC<OpsTatDaysLedgerProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const summary = store.summaryResult;
    const headline = summary?.data.headline;
    const stages = summary?.data.stages ?? [];
    const selectedPath = store.appliedFilters.path;
    const hasHeadlineCohort = (headline?.orders ?? 0) > 0;

    const openMetric = async (
      metricId: OpsTatMetricId,
      cardTitle?: string,
    ) => {
      try {
        await store.openDrawer(metricId, {
          cardTitle,
          cardSection: "days_ledger",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const changePath = async (path: OpsTatPath) => {
      try {
        if (path === store.appliedFilters.path) {
          return;
        }
        await store.changePath(path);
      } catch (error) {
        // summaryError is set on the store
      }
    };

    const onTileKeyDown = (
      event: React.KeyboardEvent,
      metricId: OpsTatMetricId,
    ) => {
      try {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void openMetric(metricId);
        }
      } catch (error) {
        // ignore keyboard handler failures
      }
    };

    const renderPathToggle = () => (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        {PATH_OPTIONS.map((option) => {
          const selected = selectedPath === option.value;
          return (
            <AppButton
              key={option.value}
              label={option.label}
              variant={selected ? "primary" : "transparentPrimary"}
              disabled={store.summaryLoading}
              fontSize="0.75rem"
              onClick={() => {
                void changePath(option.value);
              }}
              customStyles={{
                minHeight: 32,
                px: 1.5,
              }}
            />
          );
        })}
      </Box>
    );

    const renderLegend = () => (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="ok" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Within SLA
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="warn" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            At risk
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="bad" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Breached
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 8,
              border: "1px solid",
              borderColor: customColors.lightGrayBorder,
              background: `repeating-linear-gradient(115deg, ${customColors.veryLightGray} 0 4px, ${customColors.white} 4px 7px)`,
            }}
          />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Committed SLA
          </CustomTypography>
        </Box>
      </Box>
    );

    const renderSpine = () => {
      const hatch = `repeating-linear-gradient(115deg, ${customColors.veryLightGray} 0 4px, ${customColors.white} 4px 7px)`;
      const spineMinWidth = Math.max(
        getOpsTatSpineTrackMinWidth(stages, (stage) => stage.actual?.mean),
        getOpsTatSpineTrackMinWidth(stages, (stage) => stage.target),
      );

      return (
        <Box
          sx={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            mx: { xs: -1, sm: 0 },
            px: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ minWidth: spineMinWidth, width: "100%" }}>
            <Box
              sx={{ display: "flex", height: { xs: 32, md: 38 }, gap: "2px" }}
            >
              {stages.map((stage) => (
                <SpineActualSegment
                  key={stage.stageId}
                  stage={stage}
                  onOpen={() => {
                    void openMetric(
                      stage.stageId as OpsTatMetricId,
                      stage.name,
                    );
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", height: 12, gap: "2px", mt: "5px" }}>
              {stages.map((stage) => (
                <Box
                  key={`${stage.stageId}-plan`}
                  sx={{
                    flex: getOpsTatSpineFlex(stage.target || 0),
                    minWidth: OPS_TAT_SPINE_MIN_PX,
                    borderRadius: "2px",
                    border: "1px solid",
                    borderColor: customColors.lightGrayBorder,
                    background: hatch,
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "2px",
                mt: "7px",
              }}
            >
              {stages.map((stage) => {
                const colors = getOpsTatStatusColors(theme, stage.status);
                return (
                  <Box
                    key={`${stage.stageId}-tick`}
                    sx={{
                      flex: getOpsTatSpineFlex(stage.actual?.mean),
                      minWidth: OPS_TAT_SPINE_MIN_PX,
                      overflow: "hidden",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.15,
                      pr: 0.25,
                    }}
                    role="button"
                    tabIndex={0}
                    title={`${stage.code} ${stage.short} · ${stage.name}`}
                    onClick={() => {
                      void openMetric(
                        stage.stageId as OpsTatMetricId,
                        stage.name,
                      );
                    }}
                    onKeyDown={(event) =>
                      onTileKeyDown(event, stage.stageId as OpsTatMetricId)
                    }
                  >
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={customColors.navyText}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                      }}
                    >
                      {stage.code}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={customColors.navyText}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                      }}
                    >
                      {stage.short}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                      sx={{
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                      }}
                    >
                      {formatOpsTatDays(stage.actual?.mean)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                      sx={{
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                      }}
                    >
                      Median {formatOpsTatDays(stage.actual?.p50)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={colors.fg}
                      sx={{
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                      }}
                    >
                      {formatOpsTatSignedDays(stage.delta)}
                    </CustomTypography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    };

    const renderLedgerBody = () => {
      if (store.summaryError && !summary) {
        return <OpsTatInlineState message={store.summaryError} />;
      }

      if (store.summaryLoading && !summary) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="10rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (!headline || !hasHeadlineCohort) {
        return (
          <OpsTatInlineState message="No orders in the current filters." />
        );
      }

      const cycleStatus = headline.status;
      const topDrag = headline.top_drag;

      return (
        <>
          <Grid2 container spacing={0} columns={12}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box
                role="button"
                tabIndex={0}
                sx={{
                  ...clickableSx,
                  p: 2,
                  height: "100%",
                  "&:hover": { bgcolor: theme.palette.action.hover },
                }}
                onClick={() => {
                  void openMetric("cycle_total", "Cycle total");
                }}
                onKeyDown={(event) => onTileKeyDown(event, "cycle_total")}
              >
                <CustomTypography
                  variant="caption"
                  color={customColors.greyTextColor}
                  sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
                >
                  Mean order → dispatch ready ·{" "}
                  {formatOpsTatPathEyebrow(headline.path)}
                </CustomTypography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    mt: 0.75,
                  }}
                >
                  <CustomTypography
                    variant="heading1"
                    color={customColors.navyText}
                    sx={{
                      fontSize: { xs: "2rem", md: "2.6rem" },
                      letterSpacing: "-0.03em",
                      lineHeight: 0.9,
                    }}
                  >
                    {formatOpsTatNumber(headline.cycle?.mean)}
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color={customColors.greyTextColor}
                    sx={{ pb: 0.5 }}
                  >
                    days
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1.25,
                  }}
                >
                  <Box sx={getOpsTatStatusPillSx(theme, cycleStatus)}>
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={getOpsTatStatusColors(theme, cycleStatus).fg}
                    >
                      {formatOpsTatDeltaLabel(headline.delta)}
                    </CustomTypography>
                  </Box>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    SLA{" "}
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatDays(headline.target)}
                    </Box>
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    Median{" "}
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatDays(headline.cycle?.p50)}
                    </Box>
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    P90{" "}
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatDays(headline.cycle?.p90)}
                    </Box>
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatCount(headline.orders)}
                    </Box>{" "}
                    orders
                  </CustomTypography>
                  {headline.measured != null || headline.in_flight != null ? (
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                    >
                      <Box
                        component="span"
                        sx={{ color: customColors.navyText, fontWeight: 600 }}
                      >
                        {formatOpsTatCount(headline.measured)}
                      </Box>{" "}
                      measured ·{" "}
                      <Box
                        component="span"
                        sx={{ color: customColors.navyText, fontWeight: 600 }}
                      >
                        {formatOpsTatCount(headline.in_flight)}
                      </Box>{" "}
                      in flight
                    </CustomTypography>
                  ) : null}
                </Box>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Grid2 container spacing={0} columns={12} sx={{ height: "100%" }}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="On-SLA orders"
                    value={formatOpsTatPercent(headline.sla_adherence_pct)}
                    detail={
                      headline.breached == null
                        ? headline.measured != null ||
                          headline.in_flight != null
                          ? `${formatOpsTatCount(headline.measured)} measured · ${formatOpsTatCount(headline.in_flight)} in flight`
                          : "No fully-ready orders in this window"
                        : `${formatOpsTatCount(headline.breached)} of ${formatOpsTatCount(headline.measured ?? headline.orders)} breached`
                    }
                    status={cycleStatus}
                    onClick={() => {
                      void openMetric("sla_adherence", "On-SLA orders");
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "sla_adherence")}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="Top drag"
                    value={topDrag?.name || "—"}
                    compactValue
                    detail={
                      topDrag
                        ? `${formatOpsTatSignedDays(topDrag.delta)} over, ${formatOpsTatPercent(topDrag.share_of_overrun_pct)} of overrun`
                        : "No stage overrun in this window"
                    }
                    status={null}
                    onClick={() => {
                      void openMetric(
                        "top_drag",
                        topDrag?.name
                          ? `Top drag · ${topDrag.name}`
                          : "Top drag",
                      );
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "top_drag")}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="Customised"
                    value={formatOpsTatPercent(headline.custom_mix_pct)}
                    detail={`${formatOpsTatCount(headline.orders)} orders in cohort`}
                    status={null}
                    onClick={() => {
                      void openMetric("custom_mix", "Customised mix");
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "custom_mix")}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 1.5,
              }}
            >
              {renderLegend()}
            </Box>
            {stages.length > 0 ? (
              renderSpine()
            ) : (
              <OpsTatInlineState message="No stage means for the current path." />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
                borderTop: "1px dashed",
                borderColor: "divider",
                mt: 1.5,
                pt: 1,
                pb: 1.5,
              }}
            >
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                DAY 0 · order confirmed
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                hatched bar = committed SLA · solid bar = actual mean
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                DAY {formatOpsTatNumber(headline.cycle?.mean)} · ready to
                dispatch
                {headline.cycle
                  ? ""
                  : ` · ${formatOpsTatCount(headline.in_flight)} in flight`}
                {summary?.meta.generated_at
                  ? ` · ${formatOpsTatAsOf(summary.meta.generated_at)}`
                  : ""}
              </CustomTypography>
            </Box>
          </Box>
        </>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.summaryLoading && summary ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <CustomTypography variant="heading3" sx={{ mb: 0.5 }}>
          Days ledger
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={customColors.greyTextColor}
          sx={{ mb: 2 }}
        >
          Every stage drawn to the width of the days it actually consumes, on
          top of its committed SLA.
        </CustomTypography>

        <Box sx={{ mb: 2 }}>{renderPathToggle()}</Box>

        {store.summaryError && summary ? (
          <Box sx={{ mb: 2 }}>
            <OpsTatInlineState message={store.summaryError} />
          </Box>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {renderLedgerBody()}
        </Paper>
      </Paper>
    );
  },
);

const SpineActualSegment = ({
  stage,
  onOpen,
}: {
  stage: OpsTatStageCard;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const colors = getOpsTatStatusColors(theme, stage.status);
  const actual = stage.actual?.mean;
  const labelColor =
    stage.status === "warn" || stage.status == null
      ? theme.palette.customColors.navyText
      : theme.palette.customColors.white;

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`${stage.name} ${formatOpsTatDays(actual)}`}
      title={`${stage.code} · ${stage.short} · ${stage.name}`}
      sx={{
        flex: getOpsTatSpineFlex(actual),
        minWidth: OPS_TAT_SPINE_MIN_PX,
        border: 0,
        borderRadius: "2px",
        bgcolor: colors.fg,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        overflow: "hidden",
        "&:hover": { filter: "brightness(1.08)" },
      }}
    >
      <CustomTypography variant="caption" fontWeight={600} color={labelColor}>
        {formatOpsTatNumber(actual)}
      </CustomTypography>
    </Box>
  );
};

const KpiTile = ({
  eyebrow,
  value,
  detail,
  status,
  compactValue,
  onClick,
  onKeyDown,
}: {
  eyebrow: string;
  value: string;
  detail: string;
  status: OpsTatStatus | undefined;
  compactValue?: boolean;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      sx={{
        ...clickableSx,
        p: 2,
        height: "100%",
        borderLeft: { xs: "none", md: "1px solid" },
        borderTop: { xs: "1px solid", sm: "none", md: "none" },
        borderColor: "divider",
        "&:hover": { bgcolor: theme.palette.action.hover },
      }}
    >
      <CustomTypography
        variant="caption"
        color={customColors.greyTextColor}
        sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
      >
        {eyebrow}
      </CustomTypography>
      <CustomTypography
        variant="heading4"
        color={customColors.navyText}
        sx={{
          mt: 0.75,
          mb: 0.5,
          fontSize: compactValue ? "1rem" : "1.4rem",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </CustomTypography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <StatusDot status={status} />
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          {detail}
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default OpsTatDaysLedger;
